export class NumberFunctions {
  public static roundToPlaces(num, places = 2) {
    const roundingModifier = Math.pow(10, places);
    return Math.round((num + Number.EPSILON) * roundingModifier) / roundingModifier;
  }

  public static range(start: number, stop: number, step = 1) {
    if (step === 0) throw new Error('Step is zero');

    const length = (stop - start) / step + 1;
    if (length <= 0) throw new Error('Invalid array length');

    return Array.from({ length }, (_, i) => start + i * step);
  }

  /**
   * Normalize a numeric object to percentages that sum to exactly 100.
   *
   * This function converts a given record of numeric values into a set of integer percentages.
   * The total sum of the returned values will always be 100, and rounding is handled using
   * the Hamilton method (largest remainder). Negative values are treated as zero.
   *
   * Example:
   * ```ts
   * normalizeToPercentage({ easy: 2, medium: 4, hard: 3, unknown: 1 })
   * // returns: { easy: 20, medium: 40, hard: 30, unknown: 10 }
   * ```
   *
   * If all values are zero or negative, the result will be an object with 0 for all keys.
   *
   * @template T A record where all values are numeric.
   * @param values - An object with numeric values to normalize.
   * @returns A new object of the same shape, with integer percentages that sum to 100.
   */
  public static normalizeToPercentage<T extends { [K in keyof T]: number }>(values: T): T {
    // Clamp negative values to zero
    const clamped = {} as T;
    Object.keys(values).forEach((key) => {
      const typedKey = key as keyof T;
      const keyValue = values[typedKey];

      clamped[typedKey] = Math.max(0, keyValue || 0) as T[keyof T];
    });

    const total = Object.values(clamped).reduce((sum: number, value: number) => sum + value, 0) as number;
    if (isNaN(total)) return {} as T;

    if (total === 0) {
      const result = {} as T;
      Object.keys(clamped).forEach((key) => {
        result[key as keyof T] = 0 as T[keyof T];
      });
      return result;
    }

    const rawPercentages = {} as Record<keyof T, number>;
    const floored = {} as Record<keyof T, number>;
    const remainders: [keyof T, number][] = [];

    Object.keys(clamped).forEach((key) => {
      const typedKey = key as keyof T;
      const raw = (clamped[typedKey] / total) * 100;
      const floor = Math.floor(raw);

      rawPercentages[typedKey] = raw;
      floored[typedKey] = floor;
      remainders.push([typedKey, raw - floor]);
    });

    const currentTotal = Object.values(floored).reduce((sum: number, val: number) => sum + val, 0) as number;
    const remainderPoints = 100 - currentTotal;

    // Distribute leftover points to values with the largest remainders
    remainders.sort((a, b) => b[1] - a[1]);

    for (let i = 0; i < remainderPoints; i++) {
      const key = remainders[i % remainders.length][0];
      floored[key]++;
    }

    return floored as T;
  }
}
